import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService } from '@ebw-monorepo/login';
import { EbwModalService } from '@ebw-monorepo/modal';
import { NavigationService } from '@ebw-monorepo/navigation';
import { PermissionService, SplitViewService, UtilsService } from '@ebw-monorepo/utils';
import { PsaService } from 'libs/ng-components/map/src/lib/services/psa.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../../libs/ng-components/utils/src/lib/services/api-url.service';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  automateRefreshAlreadyTriggered$ = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private utilsService: UtilsService,
    private loginService: LoginService,
    private apiUrlService: ApiUrlService,
    private configService: ConfigService,
    private navigation: NavigationService,
    private _splitViewService: SplitViewService,
    private modalService: EbwModalService,
    private psaService: PsaService,
    private _permissionService: PermissionService
  ) {}

  logout() {
    const refresh = this.loginService.getRefresh() ?? '';
    const isEntra = this.loginService.getEntra();

    if (isEntra) {
      this.loginService.clearToken();
      this.loginService.clearEntra();
      this.configService.removeConfiguration();
      this.navigation.clearHistorySearch();
      this._splitViewService.destroy();
      this.psaService.hideDuplicatePsa();
      this.psaService.closeCreateInterconnectionPanel();
      this._logoutWithMicrosoft(refresh, false);
      return;
    }

    if (this.loginService.userIsLoggedIn()) {
      this._logoutFromRemote();
    } else {
      this._logoutLocal();
    }
  }

  private _logoutWithMicrosoft(refresh: string, isAdmin: boolean) {
    const url = this.apiUrlService.createUrl(`/auth/entra/logout?refresh_token=${refresh}&is_admin=${isAdmin}`);
    window.location.href = url;
  }

  _logoutFromRemote() {
    const API_URL = this.apiUrlService.createUrl(API_URLS.AUTH.LOGOUT);
    const refreshObj = { refreshToken: this.loginService.getRefresh() };
    this.utilsService.postRequest(API_URL, refreshObj, false, {}, 'application/json').subscribe({
      next: () => {
        this._logoutLocal();
        this.modalService.openSuccessModal({
          title: 'Logout',
          content: 'Logout avvenuto'
        });
      },
      error: () => {
        this._logoutLocal();
        this.modalService.openErrorModal({
          title: 'Error',
          content: 'Errore logout'
        });
      }
    });
  }

  _logoutLocal() {
    this.loginService.clearToken();
    this.configService.removeConfiguration();
    this.navigation.clearHistorySearch();
    this.router.navigate(['/login']);
    this._splitViewService.destroy();
    // PSA
    this.psaService.hideDuplicatePsa();
    this.psaService.closeCreateInterconnectionPanel();
  }

  entraExchangeSession(sessionCode: string): Observable<any> {
    const API_URL = this.apiUrlService.createUrl(`/auth/entra/session/${sessionCode}`);
    return this.utilsService.getRequestNoAuth(API_URL, {});
  }

  login(username: string, password: string): Observable<any> {
    const API_URL = this.apiUrlService.createUrl(API_URLS.AUTH.LOGIN);

    const body = new HttpParams()
      .set('username', username)
      .set('password', password)
      .set('grant_type', 'password')
      .set('client_id', 'web')
      .set('client_secret', 'secret')
      .set('scope', 'login');

    const HEADERS = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    return this.utilsService.postRequestNoAuth(API_URL, body, false, { headers: HEADERS });
  }

  // ------------------------------------------------------------------------------------------------

  refreshToken() {
    const url = this.apiUrlService.createUrl(API_URLS.AUTH.REFRESH_TOKEN);
    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('refresh_token', this.loginService.getRefresh())
      .set('client_id', 'web')
      .set('client_secret', 'secret');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: 'Bearer ' + this.loginService.getToken()
    });

    return this.utilsService.postRequest(url, body, false, { headers });
  }

  // ------------------------------------------------------------------------------------------------

  automateRefresh(interval: number) {
    // continuo il refresh solo se l'utente è loggato
    if (!this.loginService.userIsLoggedIn()) {
      return;
    }
    setTimeout(() => {
      this.refreshToken().subscribe({
        next: (res) => {
          this._permissionService.decodePermissions(res.access_token);
          // sovrascrivo i nuovi token nel servizio login
          this.loginService.setToken(res.access_token);
          this.loginService.setRefresh(res.refresh_token);
          this.loginService.setTokenExpireDate(res.expires);
          // reinizializzo il timeout
          this.automateRefresh(res.expires_in);
        },
        error: (err) => {
          console.error('[refreshToken]', err.message);
        }
      });
    }, interval * 0.75 * 1000);
  }
}
